import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { arrayToObject, sortBy } from '../../../utils/core'
import FormField from '../../form/FormField'
import Form from '../../form/Form'
import Button from '../../elements/Button'
import Radio from '../../elements/Radio'
import Select from '../../elements/Select'
import { bundleBuilderFieldPropType } from '../../../propTypes'
import { optOutReasons, fieldType, sortByText, validationResults, regex, supportTicketMessage, eccCbmHelpDocumentURL, nccCbmHelpDocumentURL, cbm, comcastBusinessMobile } from '../../../constants/configuration'

const BundleBuilderForm = ({ onCancel, onProceed, ...props }) => {
  const [bundleForm, setBundleForm] = useState(null)
  const eligibleSystemCodes = props.eligibleSystemCodes || []
  const [fields, setFields] = useState(arrayToObject(props.fields))
  const [errors, setErrors] = useState({})
  const groupedFields = Object.values(fields).
    sort(sortBy(sortByText)).reduce((acc, field) => {
      const { categoryName } = field
      acc[categoryName] = acc[categoryName] || []
      acc[categoryName].push(field)
      return acc
    }, {})
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [isSupportTicketChecked, setIsSupportTicketChecked] = useState(false)
  const pageId = props.pageId || ''
  const [pageIdsNotes, setPageIdsNotes] = useState([])
  const [pageIdsComment, setPageIdsComment] = useState([])
  const [pageIdsProceedToBB, setPageIdsProceedToBB] = useState([])
  const CBMHelpDocumentURL = props.customerType?.toLowerCase() === 'ec' ?
    eccCbmHelpDocumentURL :
    nccCbmHelpDocumentURL

  const [pageIdsSupportTicket, setPageIdsSupportTicket] = useState([])
  useEffect(() => {
    const hasErrors = Object.values(errors).some(error => error)
    const fieldArray = Object.values(fields).sort(sortBy(sortByText))
    const isInvalid = hasErrors || fieldArray.every(({ isSelected,
      isCommentRequired, comment }) => {
      if (isSelected && isCommentRequired) {
        return !comment || comment.trim() === ''
      }
      return !isSelected
    })
    const initialForm = {
      fields: { ...fields },
      isInvalid
    }
    setBundleForm(initialForm)
  }, [fields, errors])
  const [isCBMLinksVisible, setIsCBMLinksVisible] = useState(false)

  function handleOnFieldChange(id, target) {
    props.onCBMScroll(false)
    setIsCBMLinksVisible(false)
    const { type, value, categoryName } = target
    switch (type) {
      case fieldType.dropDown:
        updateDropdownSelection(value)
        break
      case fieldType.radio:
        updateOptOutReasonSelection(id, categoryName)
        break
      default:
        updateFieldComment(id, value)
        break
    }
  }

  function updateDropdownSelection(value) {
    setFields(state =>
      Object.entries(state).reduce((acc, [key, field]) => {
        acc[key] = {
          ...field,
          isSelected: field.id === Number(value)
        }
        return acc
      }, {})
    )
    const selectedField = Object.values(fields).
      find(field => field.id === Number(value))
    updateWorkFlowStep(selectedField)
  }

  function updateOptOutReasonSelection(id, categoryName) {
    props.onCBMScroll(false)
    setSelectedGroup(categoryName)
    setFields(prevState =>
      Object.entries(prevState).reduce((acc, [key, field]) => {
        acc[key] = {
          ...field,
          isSelected: field.id === id,
          ...(field.id === id && { comment: '' })
        }
        return acc
      }, {})
    )
    const selectedField = Object.values(fields).
      find(field => field.id === id)
    updateWorkFlowStep(selectedField)
  }

  function updateWorkFlowStep(selectedField) {
    if (!selectedField) {
      setPageIdsNotes([])
      setPageIdsComment([])
      setPageIdsProceedToBB([])
      setPageIdsSupportTicket([])
      return
    }

    const parseStep = val => {
      if (!val) return []
      return Array.isArray(val)
        ? val
        : String(val).split(',').map(s => s.trim()).filter(Boolean)
    }
    setPageIdsNotes(parseStep(selectedField.notesWorkFlowStep))
    setPageIdsComment(parseStep(selectedField.commentWorkFlowStep))
    setPageIdsProceedToBB(parseStep(selectedField.proceedToBBWorkFlowStep))
    setPageIdsSupportTicket(parseStep(selectedField.supportTicketWorkFlowStep))
  }

  function updateFieldComment(id, value) {
    setFields(prevState => ({
      ...prevState,
      [id]: {
        ...prevState[id],
        comment: value
      }
    }))
    const isCBM =
      value.toLowerCase().includes(cbm) ||
      value.toLowerCase().includes(comcastBusinessMobile)
    if (isCBM && props?.cbmLabelRef?.current) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      props.onCBMScroll(true)
      setIsCBMLinksVisible(true)
    }
    else {
      props.onCBMScroll(false)
      setIsCBMLinksVisible(false)
    }
  }

  function checkFieldError({ field }) {
    if (field && field.name) {
      setErrors(previousErrors => ({
        ...previousErrors,
        [field.name]: !!field.error
      }))
    }
  }

  function handleValidation(value, field) {
    const digitsOnly = value.replace(regex.isNonDigits, '')
    switch (field) {
      case optOutReasons.existingCustomer:
      case optOutReasons.move:
        return validateBAN(digitsOnly, value)
      case optOutReasons.marketingOffer:
        return validateOfferId(value)
      case optOutReasons.applicationIssue:
        return validateSRTicket(value)
      default:
        return false
    }
  }

  function validateBAN(digitsOnly, value) {
    if(value)
    {
      if (!regex.isNumber.test(value)) {
        return validationResults.validateBan
      }
      if (!regex.is16Digits.test(value)) {
        return validationResults.validateBanFormat
      }
    }
    if(digitsOnly)
    {
      if (!digitsOnly.startsWith('8')) {
        return validationResults.validateBanFirstDigit
      }
      if (digitsOnly.length !== 16) {
        return validationResults.validateBanLength
      }
      if (value && !eligibleSystemCodes.includes(digitsOnly.slice(0, 4))) {
        return validationResults.validateSystemCode
      }
    }
    return false
  }

  function validateOfferId(value) {
    if (value && !regex.isNumber.test(value)) {
      return validationResults.validateOfferId
    }
    return false
  }

  function validateSRTicket(value) {
    if (value && value.trim().length < 10) {
      return validationResults.validateSRMinLength
    }
    return false
  }

  function renderField(field) {
    const hasNotes = pageIdsNotes.includes(pageId) && field.notes
    const hasComment = pageIdsComment.includes(pageId) 
    && field.isCommentRequired
    const hasSupportTicket = field.supportTicketWorkFlowStep !== null && 
      pageIdsSupportTicket.includes(pageId) &&
      !props.isOrionSales
    return (
      <StyledFieldContainer key={field.id}>
        <StyledRadioContainer>
          <Radio
            name={`${field.id}-radio`}
            checked={field.isSelected}
            onChange={({target}) => handleOnFieldChange(field.id, target)}
            ariaLabel={field.text}
          >
            {field.text}
          </Radio>
        </StyledRadioContainer>
        {field.isSelected && (
          <>
            {hasNotes && (
              <StyledNotesContainer>
                <strong style={{ color: 'black' }}>{field.notes}</strong>
              </StyledNotesContainer>
            )}
            {hasSupportTicket && (
              <StyledSupportTicketContainer>  
                <strong>
                  {supportTicketMessage}
                </strong>
                <StyledSupportTicketCheckbox
                  type="checkbox"
                  id="supportTicketCheckbox"
                  checked={isSupportTicketChecked}
                  onChange={e => setIsSupportTicketChecked(e.target.checked)}
                />
              </StyledSupportTicketContainer>
            )}
            {hasComment && (
              <StyledTextAreaContainer>
                <StyledLabel>
                  <span>&#42;</span> 
                  <strong>{hasSupportTicket && isSupportTicketChecked ? 
                    field.supportTicketCommentLabel : field.commentLabel}
                  </strong>:
                </StyledLabel>
                <StyledTextArea
                  name={`${field.id}-textarea`}
                  placeholder={field.commentPlaceholder}
                  value={field.comment}
                  onFocus={({ target }) => 
                    handleOnFieldChange(field.id, target)}
                  error={handleValidation(field.comment, field.text)}
                  isSubmitted
                  onChange={({ target }) => 
                    handleOnFieldChange(field.id, target)}
                  ariaLabel={field.commentLabel}
                />
                {isCBMLinksVisible && (
                  <>
                    <strong>
                      The CBM option is available on this page.<br />
                      Close this popup to access the CBM section.
                    </strong>
                    <div>
                      <Button
                        type="link"
                        onClick={() => window.open(CBMHelpDocumentURL, '_blank', 'noopener,noreferrer')}
                      >
                        CBM Help Document 
                      </Button>
                    </div>
                  </>
                )}
              </StyledTextAreaContainer>
            )}
          </>
        )}
      </StyledFieldContainer>
    )
  }

  function renderFieldGroup(categoryName, fieldGroup) {
    const isGroupSelected = selectedGroup === categoryName
    if (fieldGroup.length > 1) {
      return (
        <StyledFieldContainer key={categoryName}>
          <StyledRadioContainer>
            <Radio
              name={`${categoryName}-radio`}
              checked={isGroupSelected}
              onChange={( ) => updateOptOutReasonSelection(
                null, categoryName)}
              ariaLabel={categoryName}
            >
              {categoryName}
            </Radio>
          </StyledRadioContainer>
          {isGroupSelected && (
            <StyledDropdownContainer>
              <Dropdownlabel>
                <span>*</span> <strong> Product List</strong>:
              </Dropdownlabel>
              <StyledSelect
                value={fieldGroup.find(field => field.isSelected)?.id || ''}        
                onChange={({ target }) => handleOnFieldChange(null, target)}
                ariaLabel={`Select ${categoryName}`}
              >
                <option
                  value=""
                  disabled
                >
                  Please select the product
                </option>
                {fieldGroup.map(field => (
                  <option
                    key={field.id}
                    value={field.id}
                  >
                    {field.text}
                  </option>
                ))}
              </StyledSelect>
            </StyledDropdownContainer>
          )}
        </StyledFieldContainer>
      )
    }
    return fieldGroup.map(field => renderField(field))
  }

  return (
    <Form
      name="bundle-builder"
      form={bundleForm}
      isSubmitted
      onChange={({ form, field }) => {
        checkFieldError({ field })
        setBundleForm(form)
      }}
    >
      <h3>
        Please select your reason for routing to Bundle Builder for this order:
      </h3>
      <StyledFormContainer>
        {Object.entries(groupedFields).map(([categoryName, fieldGroup]) =>
          renderFieldGroup(categoryName, fieldGroup)
        )}
      </StyledFormContainer>

      <StyledLabel>
        <span>&#42;</span>
        <strong>At least one reason must be selected to proceed</strong>
      </StyledLabel>
      <StyledFooter>
        <StyledButton
          type="primary"
          onClick={onCancel}
        >
          Cancel
        </StyledButton>
        <StyledButton
          type="primary"
          disabled={!bundleForm || bundleForm.isInvalid
            || !pageIdsProceedToBB.includes(pageId)
          }
          onClick={() => onProceed(fields, false, isSupportTicketChecked)}
        >
          Proceed To Bundle Builder
        </StyledButton>
      </StyledFooter>
    </Form>
  )
}

BundleBuilderForm.propTypes = {
  fields: PropTypes.arrayOf(bundleBuilderFieldPropType),
  onCancel: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired,
  eligibleSystemCodes: PropTypes.arrayOf(PropTypes.string),
  pageId: PropTypes.string,
  cbmLabelRef: PropTypes.object,
  onCBMScroll: PropTypes.func,
  customerType: PropTypes.string,
  isOrionSales: PropTypes.bool
}

BundleBuilderForm.defaultProps = {
  fields: []
}

const StyledFormContainer = styled.div`
  margin: 30px 0;
`

const StyledFieldContainer = styled.div`
  margin-bottom: 1rem;
`

const StyledRadioContainer = styled.div`
  display: flex;
  align-items: center;

  > :first-child {
    margin-right: 10px;
  }
`

const StyledTextAreaContainer = styled.div`
  margin: 10px auto;
  width: 80%;
`

const StyledNotesContainer = styled.div`
  margin: 10px 0;
  width: 80%;
  margin-left: 40px;
`

const StyledSupportTicketContainer = styled.div`
  margin: 10px 0 10px 40px;
`

const StyledSupportTicketCheckbox = styled.input`
  margin: 5px 0 0 10px;
`

const StyledLabel = styled.div`
  margin: 0 0 5px 0;

  span {
    color: ${({ theme }) => theme.colors?.danger};
  }
`

const StyledTextArea = styled(FormField.TextArea)`
  textarea {
    height: 95px;
  }
`

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`

const StyledButton = styled(Button)`
  text-transform: uppercase;
  margin-left: 10px;
`
const StyledDropdownContainer = styled.div`
  margin: 10px 0;
`
const StyledSelect = styled(Select)`
  flex: 1;
  min-width: 23rem;
  max-width: 30rem;
  padding: 6px 25px 6px 10px;
  height: auto;
  line-height: 1.2;
  font-size: 0.8rem;
  margin-left: 40px;
`
const Dropdownlabel = styled.div`
  font-weight: 800;
  margin-left: 40px;
  span {
    color: ${({ theme }) => theme.colors?.danger};
  }
`

export default BundleBuilderForm
